import {toast} from 'react-toastify';
import {observer} from 'mobx-react-lite';
import React, {FC, useState} from 'react';
import {PencilSquareIcon, TrashIcon} from '@heroicons/react/24/outline';
import {IProfile, VerdocsEndpoint, deleteOrganizationContact} from '@verdocs/js-sdk';
import {Card, RowAction, SectionTitle, Spinner} from '../../../Components';
import {useOrganizationContacts} from '../../../react-sdk/queries';
import {EditContactDialog, OkCancelDialog} from '../../../Dialogs';
import {getCallerAccess, memberName} from './memberAccess';
import {AppState} from '../../../AppState';

export const ContactsTable: FC = observer(() => {
  const organizationId = AppState.currentProfile?.organization_id;
  const {isAdmin} = getCallerAccess();

  const [working, setWorking] = React.useState(false);
  const [deleting, setDeleting] = useState<IProfile | null>(null);
  const [editingContact, setEditingContact] = useState<IProfile | null>(null);
  const contactsQuery = useOrganizationContacts(organizationId);
  const contacts = contactsQuery.data || [];

  if (contactsQuery.isLoading) {
    return <Spinner mode="dark" />;
  }

  if (contactsQuery.isError) {
    return (
      <Card>
        <div className="text-[13px] text-gray-500">Unable to load organization contacts. Please try again later.</div>
      </Card>
    );
  }

  const handleDeleteContact = async () => {
    const target = deleting;
    if (!target) return;
    setDeleting(null);
    setWorking(true);
    try {
      await deleteOrganizationContact(VerdocsEndpoint.getDefault(), target.id);
      contactsQuery.refetch().catch((e: any) => console.log('Unknown Error', e));
      toast.info('Contact deleted.');
    } catch (e: any) {
      console.log('Error deleting contact', e);
      toast.error(e.response?.data?.error || 'Error deleting contact, please try again later.');
    }
    setWorking(false);
  };

  return (
    <>
      <Card>
        <SectionTitle>Contacts</SectionTitle>

        {!contacts.length ? (
          <div className="py-6 text-center text-[13px] text-gray-500">
            Contacts provide an address book of people to whom you send envelopes regularly. They can't sign in to your organization, and appear only in the contact picker
            when you create or send an envelope.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#ebecf0] text-[11px] uppercase tracking-wide text-gray-400">
                  <th className="py-1.5 text-left font-medium">Contact</th>
                  <th className="hidden py-1.5 pl-3 text-left font-medium tablet:table-cell">Phone</th>
                  <th className="py-1.5 pl-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact.id} className="border-b border-[#f2f3f5] last:border-0">
                    <td className="py-2.5">
                      <div className="text-[13px] font-medium text-[#33364b]">
                        {[contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.email}
                      </div>
                      <div className="truncate text-[12px] text-gray-500">{contact.email}</div>
                    </td>
                    <td className="hidden whitespace-nowrap py-2.5 pl-3 text-[12px] text-gray-500 tablet:table-cell">{contact.phone}</td>
                    <td className="py-2.5 pl-3">
                      {isAdmin && (
                        <div className="flex justify-end gap-0.5">
                          <RowAction title="Edit" onClick={() => setEditingContact(contact)}>
                            <PencilSquareIcon />
                          </RowAction>
                          <RowAction title="Delete contact" danger onClick={() => setDeleting(contact)}>
                            <TrashIcon />
                          </RowAction>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {deleting && (
        <OkCancelDialog
          title={`Delete ${memberName(deleting)}?`}
          okLabel="Delete"
          onCancel={() => setDeleting(null)}
          onOk={handleDeleteContact}>
          <div className="text-[13px] text-gray-500">They will no longer appear in the contact picker. This action cannot be undone.</div>
        </OkCancelDialog>
      )}

      {editingContact && (
        <EditContactDialog
          contact={editingContact}
          onClose={() => {
            setEditingContact(null);
            contactsQuery.refetch();
          }}
        />
      )}

      {working && (
        <div className="absolute inset-0 bg-[#0000007f] flex flex-col items-center pt-8 text-white font-lg font-semibold">
          <Spinner mode="light" />
          <div className="mt-4">Please wait...</div>
        </div>
      )}
    </>
  );
});
